import React, { useState } from 'react';
import { useTranslations } from 'use-intl';
import { toTranslationKey, tForComponent } from '@/utils/translations';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Modal from '@/UIlibrary/Modal/Modal';
import { BRAND_COLORS } from '@/UIlibrary/globalTheme';
import { Info } from 'react-feather';
import TextSectionWithFormula from './TextSectionWithFormula';

interface FinancialIndicatorsProps {
	recoveryTimeInYears?: string | null;
	economicSolvencyI?: string | null;
	economicSolvencyIi?: string | null;
	businessComprehensiveOperativeEfficiency?: string | null;
	applicantComprehensiveOperativeEfficiency?: string | null;
	calculatedNetIncomeIndex?: string | null;
	vehicleValue?: string | null;
}

const FinancialIndicators: React.FC<FinancialIndicatorsProps> = ({
	recoveryTimeInYears,
	economicSolvencyI,
	economicSolvencyIi,
	businessComprehensiveOperativeEfficiency,
	applicantComprehensiveOperativeEfficiency,
	calculatedNetIncomeIndex,
	vehicleValue
}) => {
	const t = useTranslations('VanCreditRequestSimulation');
	const [openInformationModal, setOpenInformationModal] = useState<boolean>(false);
	const [modalTitle, setModalTitle] = useState<string>('RecuperationTime');
	const [modalContent, setModalContent] = useState<React.ReactNode>();

	const IndicatorTextSection: React.FC<{
		value: string | number;
		title: string;
		onClick: () => void;
	}> = ({ value, title, onClick }) => {
		return (
			<div className="w-1/2 py-1 px-4">
				<div className="flex flex-row justify-between">
					<div className="flex flex-row gap-2">
						<Typography size="sm" weight="medium">
							{t(toTranslationKey(title))}:
						</Typography>
						<Info
							color={BRAND_COLORS.brand600}
							size={18}
							onClick={onClick}
							className="cursor-pointer"
						/>
					</div>
					<Typography size="md" weight="semibold" color="#8C8C8C">
						{value || '-'}
					</Typography>
				</div>
			</div>
		);
	};

	return (
		<>
			<div className="px-6">
				<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
					<div className={`flex flex-row items-center mb-4`}>
						<div className="grow">
							<Typography weight="semibold">{t('FinancialIndicators')}</Typography>
						</div>
					</div>
					<div className="flex flex-row flex-wrap">
						<IndicatorTextSection
							value={economicSolvencyI || '-'}
							title="EconomicSolvencyOne"
							onClick={() => {
								setOpenInformationModal(true);
								setModalTitle('EconomicSolvencyOne');
								setModalContent(
									<TextSectionWithFormula
										text={economicSolvencyI || '-'}
										title="EconomicSolvencyOne"
										numerator="EconomicSolvencyOneNumerator"
										denominator="EconomicSolvencyOneDenominator"
										t={tForComponent(t)}
									/>
								);
							}}
						/>
						<IndicatorTextSection
							value={economicSolvencyIi || '-'}
							title="EconomicSolvencyTwo"
							onClick={() => {
								setOpenInformationModal(true);
								setModalTitle('EconomicSolvencyTwo');
								setModalContent(
									<TextSectionWithFormula
										text={economicSolvencyIi || '-'}
										title="EconomicSolvencyTwo"
										numerator="EconomicSolvencyTwoNumerator"
										denominator="EconomicSolvencyTwoDenominator"
										t={tForComponent(t)}
									/>
								);
							}}
						/>
						<IndicatorTextSection
							value={businessComprehensiveOperativeEfficiency || '-'}
							title="BusinessComprehensiveOperativeEfficiency"
							onClick={() => {
								setOpenInformationModal(true);
								setModalTitle('BusinessComprehensiveOperativeEfficiency');
								setModalContent(
									<TextSectionWithFormula
										text={businessComprehensiveOperativeEfficiency || '-'}
										title="BusinessComprehensiveOperativeEfficiency"
										numerator="BusinessEfficiencyNumerator"
										denominator="BusinessEfficiencyDenominator"
										t={tForComponent(t)}
									/>
								);
							}}
						/>
						<IndicatorTextSection
							value={applicantComprehensiveOperativeEfficiency || '-'}
							title="ApplicantComprehensiveOperativeEfficiency"
							onClick={() => {
								setOpenInformationModal(true);
								setModalTitle('ApplicantComprehensiveOperativeEfficiency');
								setModalContent(
									<TextSectionWithFormula
										text={applicantComprehensiveOperativeEfficiency || '-'}
										title="ApplicantComprehensiveOperativeEfficiency"
										numerator="ApplicantEfficiencyNumerator"
										denominator="ApplicantEfficiencyDenominator"
										t={tForComponent(t)}
									/>
								);
							}}
						/>
						<IndicatorTextSection
							value={recoveryTimeInYears || '-'}
							title="RecuperationTime"
							onClick={() => {
								setOpenInformationModal(true);
								setModalTitle('RecuperationTime');
								setModalContent(
									<TextSectionWithFormula
										text={recoveryTimeInYears || '-'}
										title="RecuperationTime"
										numerator="RecuperationTimeNumerator"
										denominator="RecuperationTimeDenominator"
										t={tForComponent(t)}
									/>
								);
							}}
						/>
						<IndicatorTextSection
							value={calculatedNetIncomeIndex || '-'}
							title="CalculatedNetIncomeIndex"
							onClick={() => {
								setOpenInformationModal(true);
								setModalTitle('CalculatedNetIncomeIndex');
								setModalContent(
									<TextSectionWithFormula
										text={calculatedNetIncomeIndex || '-'}
										title="CalculatedNetIncomeIndex"
										numerator="CalculatedNetIncomeIndexNumerator"
										denominator="CalculatedNetIncomeIndexDenominator"
										t={tForComponent(t)}
									/>
								);
							}}
						/>
						<IndicatorTextSection
							value={vehicleValue || '-'}
							title="VehicleValue"
							onClick={() => {
								setOpenInformationModal(true);
								setModalTitle('VehicleValue');
								setModalContent(
									<TextSectionWithFormula
										text={vehicleValue || '-'}
										title="VehicleValue"
										numerator="VehicleValueNumerator"
										denominator="VehicleValueDenominator"
										t={tForComponent(t)}
									/>
								);
							}}
						/>
					</div>
				</div>
			</div>
			<Modal
				isOpen={openInformationModal}
				onClose={() => {
					setOpenInformationModal(false);
				}}
				closeable
				maxWidth={800}
			>
				<div className="flex flex-col gap-4">
					<div className="flex">
						<Typography weight="semibold">{t(toTranslationKey(modalTitle))}</Typography>
					</div>
					<div className="flex flex-col border-y-2 py-4">
						<Typography>{modalContent}</Typography>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default FinancialIndicators;
